import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import Enrollments from './Enrollments';
import { api } from '../services/api';
import { formatDateForDisplay, calculateAge, formatDate } from '../utils/dateUtils';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Grid,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  FamilyRestroom as FamilyRestroomIcon,
  School as SchoolIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

function MyAccount() {
  const { user, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [childrenError, setChildrenError] = useState('');

  const loadChildren = async () => {
    try {
      setChildrenLoading(true);
      const data = await api.getChildren();
      console.log('Children data:', data); // Debug log
      setChildren(data || []);
      setChildrenError('');
    } catch (error) {
      console.error('Failed to load children:', error);
      setChildrenError(error.response?.data?.message || 'Failed to load children.');
    } finally {
      setChildrenLoading(false);
    }
  };

  const handleDeleteChild = async (childId, childName) => {
    if (!window.confirm(`Are you sure you want to remove ${childName} from your account? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteChild(childId);
      setChildren(children.filter(child => child.id !== childId));
    } catch (error) {
      console.error('Failed to delete child:', error);
      setChildrenError(error.response?.data?.message || 'Failed to remove child. Please try again.');
    }
  };

  useEffect(() => {
    // Refresh profile data if needed
    if (user && !user.email) {
      setIsLoading(true);
      refreshProfile().finally(() => {
        setIsLoading(false);
      });
    }
    // Load children
    loadChildren();
  }, [user, refreshProfile]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading user information...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading user information...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
          My Account
        </Typography>

        {/* Profile Information Card */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Profile
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/edit-profile"
                variant="contained"
                startIcon={<EditIcon />}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Edit Profile
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.email || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  First Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.first_name || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Last Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.last_name || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.phone_number || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Street Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.street_address || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  City
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.city || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  State
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.state || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  ZIP Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.zip_code || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Gender
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'N/A'}
                </Typography>
              </Grid>

              {user.date_of_birth && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Date of Birth
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateForDisplay(user.date_of_birth)}
                  </Typography>
                </Grid>
              )}

              {user.created_at && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Member Since
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDate(user.created_at)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Children Section */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FamilyRestroomIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Children
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/children/add"
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                sx={{ borderRadius: 2 }}
                disabled={children.length >= 10}
                title={children.length >= 10 ? "Maximum of 10 children reached" : "Add a child to your account"}
              >
                Add Child
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {childrenError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {childrenError}
              </Alert>
            )}

            {childrenLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading children...
                </Typography>
              </Box>
            ) : children.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FamilyRestroomIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  No Children Added Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add your children to enroll them in our programs.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/children/add"
                  variant="contained"
                  startIcon={<AddIcon />}
                >
                  Add Your First Child
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {children.map((child) => (
                  <Grid item xs={12} sm={6} md={4} key={child.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2 }}>
                          {child.first_name} {child.last_name}
                        </Typography>

                        <Divider sx={{ mb: 1.5 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {child.date_of_birth && (
                            <>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Age
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {calculateAge(child.date_of_birth)} years old
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Birthday
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDateForDisplay(child.date_of_birth)}
                                </Typography>
                              </Box>
                            </>
                          )}
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Gender
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {child.gender ? child.gender.charAt(0).toUpperCase() + child.gender.slice(1) : 'N/A'}
                            </Typography>
                          </Box>
                          {child.grade && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Grade
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {child.grade}
                              </Typography>
                            </Box>
                          )}
                          {child.school && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                School
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {child.school}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>

                      <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/children/${child.id}/edit`}
                          variant="outlined"
                          startIcon={<EditIcon />}
                          size="small"
                          fullWidth
                        >
                          Edit
                        </Button>
                        <IconButton
                          onClick={() => handleDeleteChild(child.id, child.first_name)}
                          color="error"
                          size="small"
                          sx={{ border: 1, borderColor: 'error.main', borderRadius: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Enrollments Section */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SchoolIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Enrollments
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/programs"
                variant="contained"
                startIcon={<SchoolIcon />}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Browse Programs
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Enrollments />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default MyAccount;
